import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Refresh, Tv, ShoppingCart } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import { dashboardService } from '@/services/dashboardService';
import { ShopId } from '@core/store/slices/apiSlice.ts';
import { TEST_IDS } from '@core/constants/testIds';

// Apex Charts with React
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { BaseWidget } from '@/components/widgets/BaseWidget.tsx';
import { useNavigate } from 'react-router-dom';
import { useSettingsStore } from '@core/store';

interface HealthStatusData {
    totalFastLaners: number;
    unhealthyFastLaners: number;
}

interface FastLanerHealthStateProps {
    shopIdName: ShopId;
    heading: string;
    className?: string;
}

const FastLanerHealthState: React.FC<FastLanerHealthStateProps> = ({ shopIdName, heading, className }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [healthStatusData, setHealthStatusData] = useState<HealthStatusData>({
        totalFastLaners: 0,
        unhealthyFastLaners: 0
    });
    const navigate = useNavigate();
    const { darkMode } = useSettingsStore();

    const isHealthy = (healthStatus: any): boolean => {
        if (!healthStatus) return false;
        const healthyValues = ['healthy', 'connected', 'true'];

        const check = (value: string) => !value || !healthyValues.includes(value.toLowerCase());

        return !(
            check(healthStatus.wlan) ||
            check(healthStatus.hybridLoc) ||
            check(healthStatus.scanner) ||
            check(healthStatus.camera)
        );
    };

    const getHealthData = (response: any): HealthStatusData => {
        let totalFastLaners = 0;
        let unhealthyFastLaners = 0;
        const groups = response?.data ?? [];

        groups.forEach((group: any) => {
            (group.trolleys ?? []).forEach((trolley: any) => {
                totalFastLaners += 1;
                const health = trolley.healthStatus;
                if (!isHealthy(health)) {
                    unhealthyFastLaners += 1;
                }
            });
        });

        return {
            totalFastLaners,
            unhealthyFastLaners
        };
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const fastLanerTrolleyData = await dashboardService.getFastLanerTrolleyData(shopIdName.fastLanerGroupIds);
            const healthData = getHealthData(fastLanerTrolleyData);
            setHealthStatusData(healthData);
        } catch (error) {
            console.error('Failed to load FastLaner health state:', error);
            setHealthStatusData({ totalFastLaners: 0, unhealthyFastLaners: 0 });
        } finally {
            setLoading(false);
        }
    }, [shopIdName.fastLanerGroupIds]);

    useEffect(() => {
        load();

        const unsubscribe = eventBus.on(EventTypes.WIDGETS_RELOAD, load);

        return unsubscribe;
    }, [load]);

    const menuActions = [
        {
            text: t('refresh'),
            icon: <Refresh fontSize="small" />,
            onClick: load
        },
        {
            text: t('monitoring'),
            icon: <Tv fontSize="small" />,
            onClick: () => {
                navigate(`/statistics/fastLaner/health?shopId=${shopIdName.id}`);
            }
        }
    ];

    // Calculate percentage and prepare chart data
    const percentage = healthStatusData.totalFastLaners
        ? (healthStatusData.unhealthyFastLaners / healthStatusData.totalFastLaners) * 100
        : 0;

    const series = [percentage];

    const options: ApexOptions = {
        chart: {
            type: 'radialBar',
            height: 300,
            toolbar: {
                show: false
            },
            background: 'transparent',
            fontFamily: 'Wanzl_Etelka Light, Roboto, sans-serif'
        },
        plotOptions: {
            radialBar: {
                startAngle: -100,
                endAngle: 100,
                track: {
                    startAngle: -100,
                    endAngle: 100
                },
                dataLabels: {
                    value: {
                        fontSize: '25px',
                        offsetY: -10,
                        formatter: () => {
                            const unhealthy = healthStatusData.unhealthyFastLaners || '-';
                            const total = healthStatusData.totalFastLaners || '-';
                            return `${unhealthy} / ${total}`;
                        }
                    }
                }
            }
        },
        colors: darkMode ? ['#1781A5'] : ['#0099CD'],
        labels: [''],
        theme: {
            mode: 'light'
        }
    };

    return (
        <BaseWidget
            heading={heading}
            icon={<ShoppingCart />}
            className={className}
            customActions={menuActions}
            loading={loading}
            data-testid={TEST_IDS.WIDGETS.FAST_LANER.HEALTH_STATE}
        >
            <div>
                <Chart options={options} series={series} type="radialBar" height={300} />
            </div>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {t('fastLanerWithUnhealthStatus')}
                </Typography>
            </Box>
        </BaseWidget>
    );
};

export default FastLanerHealthState;
